import * as THREE from 'three';
import { CONFIG } from '../config.js';

/**
 * Manages physics state for an entity (gravity, jumping, velocity).
 */
export class PhysicsEngine {
    /** @type {THREE.Vector3} */
    #velocity = new THREE.Vector3();

    /** @type {boolean} */
    #isJumping = false;

    /** @type {boolean} */
    #canJump = true;

    /** @type {number} */
    #gravity;

    /** @type {number} */
    #jumpVelocity;

    /** @type {number} */
    #groundHeight;

    /**
     * @param {Object} options
     * @param {number} [options.gravity] - Gravity acceleration
     * @param {number} [options.jumpVelocity] - Initial jump velocity
     * @param {number} [options.groundHeight] - Height of the ground
     */
    constructor(options = {}) {
        this.#gravity = options.gravity ?? CONFIG.cat.gravity;
        this.#jumpVelocity = options.jumpVelocity ?? CONFIG.cat.jumpVelocity;
        this.#groundHeight = options.groundHeight ?? CONFIG.cat.groundHeight;
    }

    /**
     * Get current velocity
     * @returns {THREE.Vector3}
     */
    get velocity() {
        return this.#velocity;
    }

    /**
     * Check if currently jumping
     * @returns {boolean}
     */
    get isJumping() {
        return this.#isJumping;
    }

    /**
     * Check if can jump
     * @returns {boolean}
     */
    get canJump() {
        return this.#canJump;
    }

    /**
     * Get ground height
     * @returns {number}
     */
    get groundHeight() {
        return this.#groundHeight;
    }

    /**
     * Initiate a jump
     * @returns {boolean} True if jump was initiated
     */
    jump() {
        if (!this.#canJump || this.#isJumping) {
            return false;
        }

        this.#velocity.y = this.#jumpVelocity;
        this.#isJumping = true;
        this.#canJump = false;

        // Reset ability to jump after a short delay
        setTimeout(() => {
            this.#canJump = true;
        }, 1000);

        return true;
    }

    /**
     * Update physics for an entity
     * @param {THREE.Vector3} position - The entity's position to update
     * @param {number} delta - Time since last frame in seconds
     */
    update(position, delta) {
        if (this.#isJumping) {
            // Apply gravity
            this.#velocity.y -= this.#gravity;
            position.y += this.#velocity.y;

            // Check if landed
            if (position.y <= this.#groundHeight) {
                position.y = this.#groundHeight;
                this.#isJumping = false;
                this.#velocity.y = 0;
            }
        } else {
            // Ensure entity stays at ground height
            position.y = this.#groundHeight;
        }
    }

    /**
     * Reset physics state
     */
    reset() {
        this.#velocity.set(0, 0, 0);
        this.#isJumping = false;
        this.#canJump = true;
    }

    /**
     * Force land the entity (set to ground)
     * @param {THREE.Vector3} position - The entity's position
     */
    land(position) {
        position.y = this.#groundHeight;
        this.#isJumping = false;
        this.#velocity.y = 0;
    }
}
